import { MeetingProvider } from 'api/generated/types';
import type jobSettingsControlsEn from 'locales/en/recruiter/jobSettingsControls.json';
import { isEnumValue } from 'utils/typeGuards';

/** `jobSettingsControls` keys for the providers that are not vendor names. */
export const MEETING_PROVIDER_LABEL_KEYS: Partial<
  Record<string, keyof typeof jobSettingsControlsEn>
> = {
  [MeetingProvider.NONE]: 'meetingLocation.phoneCall',
  [MeetingProvider.VIDEO]: 'meetingLocation.video',
  [MeetingProvider.ONSITE]: 'meetingLocation.onsite',
};

/** Providers named after a product (Zoom, Google Meet, Teams), shown as-is in every language. */
export const VENDOR_MEETING_PROVIDERS: ReadonlySet<string> = new Set([
  MeetingProvider.TEAMS,
  MeetingProvider.GOOGLE_MEET,
  MeetingProvider.ZOOM,
]);

const VIDEO_MEETING_PROVIDERS: ReadonlySet<string> = new Set([
  MeetingProvider.VIDEO,
  MeetingProvider.TEAMS,
  MeetingProvider.GOOGLE_MEET,
]);

export const isValidMeetingProvider = (
  value: unknown
): value is MeetingProvider => isEnumValue(MeetingProvider, value);

const isVideoMeetingProvider = (value: string | null | undefined) =>
  !!value && VIDEO_MEETING_PROVIDERS.has(value);

export const displayMeetingLocationValue = (
  stored: string,
  available: string[]
): string => {
  if (
    available.includes(MeetingProvider.VIDEO) &&
    isVideoMeetingProvider(stored)
  ) {
    return MeetingProvider.VIDEO;
  }
  if (available.includes(stored)) {
    return stored;
  }
  // Leftover after switching calendar integration mode: pick the closest option.
  if (stored === String(MeetingProvider.VIDEO)) {
    for (const provider of [
      MeetingProvider.TEAMS,
      MeetingProvider.GOOGLE_MEET,
      MeetingProvider.ZOOM,
    ]) {
      if (available.includes(provider)) {
        return provider;
      }
    }
  }
  return available[0] ?? stored;
};
